import React, { Component } from 'react';
import { IconButton } from '@material-ui/core';
import PropTypes from 'prop-types';
import QueryString from 'query-string';

// Redux
import { connect } from 'react-redux';
import { setAlert } from '../../../redux/actions/alertActions';

// Server
import { listStaff, listHubs, listBadges, listStaffTypes, getNetwork } from '../../../api';

// Icons
import RefreshIcon from '@material-ui/icons/Refresh';

// Components
import StaffMemberListCard from './StaffMemberListCard';
import HubListCard from './HubListCard';
import BadgeListCard from './BadgeListCard';
import NetworkNodeListCard from './NetworkNodeListCard';
import StaffMemberInfoPanel from '../../../components/StaffMemberInfoPanel';
import HubInfoPanel from '../../../components/HubInfoPanel';
import NetworkNodeInfoPanel from '../../../components/NetworkNodeInfoPanel';
import DefaultInfoPanel from '../../../components/DefaultInfoPanel';
import PageHeader from '../../../components/PageHeader';
import FilterWidget from '../../../components/FilterWidget';
import { SearchField, ListNote, Pager, paginate, sameRows } from '../../../components/ListControls';
import { PillTrack, Pill } from '../../../components/Pill';
import DetailAside from '../../../components/DetailAside';
import DeviceHealth from './DeviceHealth';

// Constants
import { FilterOptions, UserTypes, lowBatteryPercent } from '../../../constants';
import { T, SPLIT_WIDTH } from '../../../constants/tokens';
import NetworkNode from '../../../lib/NetworkNode';
import { WEAK_QUALITY } from '../../network/format';

/**
 * The Devices screen's tabs.
 *
 * Rooms are not here: their list sits under the live map, where selecting one
 * shows it on the floor plan.
 *
 * `ready: false` marks a tab whose data source does not exist yet; it shows
 * disabled rather than hidden, so enabling one later is a flag flip. Gateways
 * and Anchors come from the mesh diagnostics behind the Network screen, and
 * like that screen are for Super users only.
 */
const DEVICE_TABS = [
  { key: 'Hubs', ready: true },
  { key: 'Badges', ready: true },
  { key: 'Gateways', ready: true, superOnly: true },
  { key: 'Anchors', ready: true, superOnly: true }
];

const ALL_STAFF = 'All staff';

/** The tab a mode opens on, and falls back to when its tab stops existing. */
const FIRST_TAB = { people: ALL_STAFF, devices: DEVICE_TABS[0].key };

/**
 * Tabs for a mode.
 *
 * The design makes staff type the tab row on the people screen rather than a
 * filter category — it is the axis you slice staff by, so it belongs in the
 * open rather than behind a popover. Which types exist is a question only the
 * `StaffType` table can answer, so the people tabs are built from what
 * `listStaffTypes()` returns rather than from a list hardcoded here.
 */
const tabsFor = (mode, staffTypes) => (mode === 'people'
  ? [{ key: ALL_STAFF, ready: true }].concat(
    staffTypes.map((type) => ({ key: type, ready: true, staffType: type }))
  )
  : DEVICE_TABS);

const FILTERS = {
  Hubs: FilterOptions.HUB_LIST,
  Badges: FilterOptions.BADGE_LIST,
  Gateways: FilterOptions.GATEWAY_LIST,
  Anchors: FilterOptions.ANCHOR_LIST
};

const filtersFor = (mode, tab) => (mode === 'people' ? FilterOptions.STAFF_LIST : FILTERS[tab]);

const FETCHERS = {
  Hubs: listHubs,
  Badges: listBadges,
  Gateways: () => getNetwork().then(({ nodes }) => nodes.filter((n) => n.isGateway())),
  Anchors: () => getNetwork().then(({ nodes }) => nodes.filter((n) => n.getKind() === NetworkNode.KINDS.ANCHOR))
};

/**
 * How much a device needs attention, for ordering a tab: 0 offline, 1 low on
 * power or on a weak link, 2 fine, 3 never reported. As the design has it:
 * red first, then yellow, then green.
 */
const ATTENTION = {
  Hubs: (hub) => (hub.getIsConnected() ? 2 : 0),
  Badges: (badge) => {
    if (!badge.getIsConnected()) return 0;
    const level = badge.getBatteryLevel();
    return level != null && level < lowBatteryPercent() ? 1 : 2;
  },
  Gateways: (node) => ({ false: 0, true: 2, null: 3 })[String(node.getIsConnected())],
  Anchors: (node) => {
    const connected = node.getIsConnected();
    if (connected === false) return 0;
    if (node.getNextHopQuality() != null && node.getNextHopQuality() < WEAK_QUALITY) return 1;
    return connected ? 2 : 3;
  }
};

/** Every people tab reads the same list and narrows it by staff type. */
const fetcherFor = (mode, tab) => (mode === 'people' ? listStaff : FETCHERS[tab]);

const SEARCHABLE = {
  Hubs: (h) => h.getSerialNumber(),
  Badges: (b) => b.getSerialNumber() + ' ' + (b.getBelongsTo()?.getName() ?? ''),
  Gateways: (n) => n.getNodeAddress() + ' ' + (n.getLabel() ?? ''),
  Anchors: (n) => n.getNodeAddress() + ' ' + (n.getLabel() ?? '') + ' ' + (n.getModel() ?? '')
};

const searchableFor = (mode, tab) => (mode === 'people'
  ? ((s) => s.getFirstName() + ' ' + s.getLastName())
  : SEARCHABLE[tab]);

/**
 * The card list screens: Devices (rooms, hubs, badges) and Staff.
 *
 * One component serves both — they differ only in which tabs they offer. The
 * search, filter, pager and detail aside are identical.
 */
class ListView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      tab: this.readLink(props.mode),
      selectedCardObject: null,
      searchCriteria: '',
      page: 1,
      data: {},
      filtered: {},
      staffCount: 0,
      staffTypes: []
    };
  }

  componentDidMount() {
    this.refresh();
    this.loadStaffTypes();

    // Refresh whichever tab is open every 5 seconds.
    this.timer = setInterval(() => this.refresh(), 5000);
  }

  /**
   * The people tabs are whatever the `StaffType` table holds, in its own
   * order. Failing to load them is not worth an error banner: the screen still
   * works, it just offers "All staff" alone until the next mount.
   */
  loadStaffTypes = () => {
    listStaffTypes()
      .then((types) => this.setState({
        staffTypes: types.map((type) => type.getName()).filter(Boolean)
      }))
      .catch(() => {
        this.setState({ staffTypes: [] });
      });
  };

  /**
   * The device health cards summarise hubs and badges regardless of which tab
   * is open, so the Devices screen loads all three rather than only the
   * visible one.
   */
  refresh = () => {
    this.load(this.state.tab);

    if (this.props.mode === 'devices') {
      if (this.state.tab !== 'Hubs') this.load('Hubs');
      if (this.state.tab !== 'Badges') this.load('Badges');
      listStaff()
        .then((staff) => this.setState({ staffCount: staff.length }))
        .catch(() => {
          // The cards fall back to 0 unassigned.
        });
    }
  };

  handleHealthSelect = (tab, filter) => {
    // Clear any active filter first, then apply the one the card stands for.
    (FILTERS[tab] ?? []).forEach((category) => {
      (category.values ?? []).forEach((v) => {
        v.active = false;
      });
    });

    if (filter) {
      const category = (FILTERS[tab] ?? []).find((c) => c.name === filter.category);
      const value = category?.values?.find((v) => v.value === filter.value);
      if (value) value.active = true;
    }

    this.handleTabChange(tab);
  };

  componentDidUpdate(prevProps) {
    if (prevProps.schoolAlertStatus !== this.props.schoolAlertStatus) {
      this.refresh();
    }
    // Staff and Devices are one component, so moving between them updates it
    // rather than mounting a new one; the link is read again here.
    if (prevProps.mode !== this.props.mode) {
      const tab = this.readLink(this.props.mode);
      this.setState({ tab, selectedCardObject: null, page: 1 }, () => this.load(tab));
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
    this.timer = null;
  }

  tabs = () => tabsFor(this.props.mode, this.state.staffTypes).filter(
    (tab) => !tab.superOnly || this.props.userType === UserTypes.SUPER
  );

  handleTabChange = (tab) => {
    this.setState(
      { tab, selectedCardObject: null, page: 1, searchCriteria: '' },
      () => this.refresh()
    );
  };

  /** Identity for reselecting the same record after a refresh. */
  idOf = (item) => {
    if (item?.getNodeAddress) return item.getNodeAddress();
    return item?.getID ? item.getID() : item?.getSerialNumber?.();
  };

  load = (tab) => {
    const fetcher = fetcherFor(this.props.mode, tab);
    if (fetcher == null) return;

    fetcher().then((rows) => {
      // A device asked for by link (or by a health chip) is selected once its
      // tab's rows arrive.
      let linked = null;
      if (this.pendingSelect != null && tab === this.state.tab) {
        linked = rows.find((row) => this.matchesId(row, this.pendingSelect)) ?? null;
        this.pendingSelect = null;
      }

      this.setState((prev) => {
        // The Devices screen also loads Hubs and Badges in the background for
        // the health cards. Only rows for the open tab can say anything about
        // the selection; matching it against another tab's rows found nothing
        // and cleared it on every refresh.
        if (tab !== prev.tab) return { data: { ...prev.data, [tab]: rows } };

        const selected = prev.selectedCardObject;
        return {
          data: { ...prev.data, [tab]: rows },
          selectedCardObject: linked ?? (selected
            ? rows.find((row) => this.idOf(row) === this.idOf(selected)) ?? null
            : null)
        };
      });
    }).catch((error) => {
      this.props.setError(error ? error : 'Could not retrieve the ' + tab.toLowerCase() + ' list.');
      this.setState((prev) => ({
        data: { ...prev.data, [tab]: [] },
        selectedCardObject: tab === prev.tab ? null : prev.selectedCardObject
      }));
    });
  };

  /** Put a renamed node back into the list it came from, and keep it selected. */
  handleNodeRenamed = (updated) => {
    const tab = this.state.tab;
    const swap = (rows) => rows?.map((row) => (row.getNodeAddress() === updated.getNodeAddress() ? updated : row));
    this.setState((prev) => ({
      data: { ...prev.data, [tab]: swap(prev.data[tab]) },
      filtered: prev.filtered[tab] ? { ...prev.filtered, [tab]: swap(prev.filtered[tab]) } : prev.filtered,
      selectedCardObject: updated
    }));
  };

  onSearchChange = (e) => this.setState({ searchCriteria: e.target.value, page: 1 });

  /** Rows for the active tab, after filters and search. Null while loading. */
  visibleRows = () => {
    const { tab } = this.state;

    // A mode change updates props before componentDidUpdate resets the tab, so
    // for one render `mode` and `tab` disagree — which previously handed hub
    // rows to the staff search and crashed the screen.
    if (!this.tabMatchesMode()) return null;

    const rows = this.state.filtered[tab] ?? this.state.data[tab];
    if (rows == null) return null;

    const regex = new RegExp(this.state.searchCriteria, 'i');
    const searchable = searchableFor(this.props.mode, tab);
    const staffType = this.props.mode === 'people' && tab !== ALL_STAFF ? tab : null;

    const visible = rows
      .filter((row) => staffType == null || row.getStaffType()?.getName() === staffType)
      .filter((row) => regex.test(searchable(row) ?? ''));

    // Devices that need attention first. Stable: equal ranks keep their order.
    const rank = this.props.mode === 'devices' ? ATTENTION[tab] : null;
    if (!rank) return visible;
    return visible
      .map((row, i) => ({ row, i, r: rank(row) }))
      .sort((a, b) => a.r - b.r || a.i - b.i)
      .map((x) => x.row);
  };

  /**
   * The tab a link asks for, and the device to select once it loads. A link
   * can open a tab with a device selected, e.g. from the Overview's health
   * chips: /dashboard/devices?tab=Hubs&select=HUB-1042
   */
  readLink(mode) {
    const query = QueryString.parse(window.location.search);
    const linked = mode === 'devices' ? DEVICE_TABS.find((t) => t.key === query.tab && t.ready) : null;
    this.pendingSelect = typeof query.select === 'string' ? query.select : null;
    return linked ? linked.key : FIRST_TAB[mode];
  }

  /** Whether a row is the device a link names, by serial number or node address. */
  matchesId = (row, id) => row.getSerialNumber?.() === id || String(this.idOf(row)) === String(id);

  /**
   * Open a tab with one device selected, e.g. from a health chip on this
   * screen. Filters on that tab are cleared, so none can hide the device.
   */
  openDevice = (tab, id) => {
    this.pendingSelect = id;
    this.handleHealthSelect(tab, null);
  };

  /**
   * The record the aside shows: the one chosen, or on a wide screen with none
   * chosen, the first card, which is the one most in need of attention. The
   * design never leaves the wide aside empty on a device tab.
   */
  shownRecord = (rows, wide) => {
    if (this.state.selectedCardObject != null) return this.state.selectedCardObject;
    if (wide && this.props.mode === 'devices' && rows != null && rows.length > 0) return rows[0];
    return null;
  };

  /** Whether the tab in state belongs to the mode in props. */
  tabMatchesMode = () => tabsFor(this.props.mode, this.state.staffTypes)
    .some((t) => t.key === this.state.tab);

  renderCard = (row, index, shown) => {
    const shared = {
      key: index,
      onClick: () => this.setState({ selectedCardObject: row }),
      selectedCardObject: shown
    };

    if (this.props.mode === 'people') return <StaffMemberListCard {...shared} staffMember={row} />;

    switch (this.state.tab) {
      case 'Hubs': return <HubListCard {...shared} hub={row} />;
      case 'Badges': return <BadgeListCard {...shared} badge={row} />;
      case 'Gateways':
      case 'Anchors': return <NetworkNodeListCard {...shared} node={row} />;
      default: return null;
    }
  };

  renderAside = (shown) => {
    const selected = shown;
    const close = () => this.setState({ selectedCardObject: null });
    if (selected == null) return <DefaultInfoPanel />;

    if (this.props.mode === 'people') {
      return <StaffMemberInfoPanel staffMember={selected} handleClose={close} />;
    }

    switch (this.state.tab) {
      case 'Hubs':
        return <HubInfoPanel hub={selected} handleClose={close} />;
      case 'Badges': {
        // A badge is shown as its holder's record, as the design does: that
        // is where its power, status and low power notices belong.
        const holder = selected.getBelongsTo?.();
        if (holder == null) return <DefaultInfoPanel message='This badge is not assigned to anyone.' />;
        holder.setBadge(selected);
        return <StaffMemberInfoPanel staffMember={holder} handleClose={close} />;
      }
      case 'Gateways':
      case 'Anchors':
        return <NetworkNodeInfoPanel node={selected} handleClose={close} onRenamed={this.handleNodeRenamed} />;
      default:
        return <DefaultInfoPanel message='No details for this record type yet.' />;
    }
  };

  render() {
    const wide = this.props.windowWidth >= SPLIT_WIDTH;
    const { tab } = this.state;
    const tabs = this.tabs();
    const rows = this.visibleRows();
    const loading = rows == null;
    const settled = this.tabMatchesMode();
    const { page, pageCount, pageRows } = paginate(rows ?? [], this.state.page);
    const shown = this.shownRecord(rows, wide);

    return (
      <div style={this.props.style}>
        <PageHeader
          title={this.props.mode === 'people' ? 'Staff' : 'Devices'}
          subtitle={loading
            ? 'Loading…'
            : (rows.length === 1 ? '1 result' : rows.length + ' results')}
        />

        {/* The health cards summarise every device type, so they sit above
            the tabs that pick one. */}
        {this.props.mode === 'devices' &&
          <DeviceHealth
            hubs={this.state.data.Hubs ?? []}
            badges={this.state.data.Badges ?? []}
            staffCount={this.state.staffCount}
            onSelect={this.handleHealthSelect}
            onOpenDevice={this.openDevice}
          />}

        {tabs.length > 1 &&
          <PillTrack scroller style={{ marginBottom: 24, maxWidth: '100%' }}>
            {tabs.map((t) => (
              <Pill
                key={t.key}
                on={settled && tab === t.key}
                disabled={!t.ready}
                title={t.ready ? undefined : 'Not available yet'}
                onClick={() => this.handleTabChange(t.key)}
              >
                {t.key}
              </Pill>
            ))}
          </PillTrack>}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: wide ? 'minmax(0, 1fr) minmax(300px, 360px)' : 'minmax(0, 1fr)',
            gap: 24,
            alignItems: 'start'
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <SearchField value={this.state.searchCriteria} onChange={this.onSearchChange} />

              {filtersFor(this.props.mode, tab) &&
                <FilterWidget
                  filterOptions={filtersFor(this.props.mode, tab)}
                  data={this.state.data[tab] ?? []}
                  updateFilteredData={(filteredData) =>
                    this.setState((prev) => ({
                      filtered: { ...prev.filtered, [tab]: filteredData },
                      // Back to page 1 when the filter changes what is listed,
                      // not on every refresh that re-runs it.
                      page: sameRows(prev.filtered[tab], filteredData, this.idOf) ? prev.page : 1
                    }))}
                />}

              <IconButton
                aria-label='Refresh'
                onClick={() => this.load(tab)}
                style={{ background: T.elevated, color: T.ink, flex: 'none' }}
              >
                <RefreshIcon style={{ fontSize: 18 }} />
              </IconButton>
            </div>

            {loading && <ListNote>Loading…</ListNote>}
            {!loading && rows.length === 0 && <ListNote>Nothing matches the current search and filters.</ListNote>}
            {!loading && pageRows.map((row, index) => this.renderCard(row, index, shown))}

            <Pager page={page} pageCount={pageCount} onChange={(next) => this.setState({ page: next })} />
          </div>

          <DetailAside
            windowWidth={this.props.windowWidth}
            open={this.state.selectedCardObject != null}
            onClose={() => this.setState({ selectedCardObject: null })}
          >
            {this.renderAside(shown)}
          </DetailAside>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  schoolAlertStatus: state.status.schoolStatus,
  isMobile: state.ui.isMobile,
  windowWidth: state.ui.windowSize.width,
  userType: state.auth.userType
});

const mapDispatchToProps = (dispatch) => ({
  setError: (msg) => setAlert(dispatch, msg)
});

export default connect(mapStateToProps, mapDispatchToProps)(ListView);

ListView.propTypes = {
  mode: PropTypes.oneOf(['devices', 'people']).isRequired,
  schoolAlertStatus: PropTypes.object,
  setError: PropTypes.func,
  style: PropTypes.object,
  isMobile: PropTypes.bool,
  windowWidth: PropTypes.number,
  userType: PropTypes.object,
};
